import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { Observable, Subject } from 'rxjs';
import { authCodeFlowConfig } from '../../environments/auth.config';
import { Profile } from '../general-components/profile/profile';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly redirectUrlStorageKey = 'redirectURL';
  readonly loginLoading = new Subject<boolean>();
  constructor(
    private oauthService: OAuthService,
    private router: Router
  ) {
    this.doConfigure();
  }

  public isLoading(): Observable<boolean> {
    return this.loginLoading.asObservable();
  }

  public login(redirectUrl: string = this.router.url) {
    this.storeRedirectUrl(redirectUrl);
    this.oauthService.initLoginFlow();
  }
  public logout() {
    this.oauthService.logOut();
    void this.router.navigate(['home']);
  }

  public getUserInfo(): Profile {
    const claims = this.oauthService.getIdentityClaims();
    const profile: Profile = new Profile(
      claims.email as string,
      claims.given_name as string,
      claims.family_name as string
    );
    return profile;
  }

  public getCognitoGroups(): string[] {
    const claims = this.oauthService.getIdentityClaims() as Record<string, unknown> | null;
    const groupsClaim = claims?.['cognito:groups'];

    if (Array.isArray(groupsClaim)) {
      return groupsClaim.filter((group): group is string => typeof group === 'string');
    }

    if (typeof groupsClaim === 'string') {
      return [groupsClaim];
    }

    return [];
  }

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();
  }

  public storeRedirectUrl(redirectUrl: string): void {
    if (this.isSafeRedirectUrl(redirectUrl)) {
      localStorage.setItem(this.redirectUrlStorageKey, redirectUrl);
    }
  }

  public consumeRedirectUrl(): string | null {
    const redirectUrl = localStorage.getItem(this.redirectUrlStorageKey);
    localStorage.removeItem(this.redirectUrlStorageKey);

    return this.isSafeRedirectUrl(redirectUrl) ? redirectUrl : null;
  }

  public redirectIfPossible(): void {
    const redirectUrl = this.consumeRedirectUrl();

    if (redirectUrl) {
      void this.router.navigateByUrl(redirectUrl);
    }
  }

  doConfigure() {
    // this is needed so we can test configure
    this.configure();
  }

  private configure() {
    this.loginLoading.next(true);
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    void this.oauthService
      .loadDiscoveryDocumentAndTryLogin({
        customHashFragment: window.location.search,
      })
      .then(() => {
        if (this.isAuthenticated()) {
          void this.oauthService.loadUserProfile();
          this.redirectIfPossible();
        }

        this.loginLoading.next(false);
      })
      .catch(() => {
        this.loginLoading.next(false);
      });
  }

  private isSafeRedirectUrl(redirectUrl: string | null): redirectUrl is string {
    return typeof redirectUrl === 'string' && redirectUrl.startsWith('/');
  }
}
